import React, { useState, useEffect, useCallback, useMemo } from "react";
import { postJsonData, getData } from "../../services/baseApi";
import { toast } from "../material/ToastPopup";
import { STORY_KEYWORDS, AD_KEYWORDS } from "./aiWriteConst";
import { CloseIcon, SparkleIcon, ChevronIcon, TYPE_ICONS } from "./aiWriteIcons";
import useLoadingDots from "./useLoadingDots";
import styles from "./AiWrite.module.scss";

const WRITE_TYPES = [
    { key: "summary", label: "요약형", desc: "핵심 정보 요약" },
    { key: "story", label: "스토리형", desc: "따스한 설명" },
    { key: "ad", label: "광고형", desc: "강력한 후킹" },
];

const MAX_KEYWORDS_STORY = 10;
const MAX_KEYWORDS_AD = 5;

const SettingStep = ({
    propertyInfo = {},
    estateCategory = "residential",
    eRandType,
    remainCount: initialRemainCount,
    onGenerated = () => {},
    onClose = () => {},
}) => {
    const [selectedType, setSelectedType] = useState("story");
    const [selectedKeywords, setSelectedKeywords] = useState([]);
    const [openCategories, setOpenCategories] = useState({});
    const [remainCount, setRemainCount] = useState(initialRemainCount);
    const [loading, setLoading] = useState(false);
    const [countLoading, setCountLoading] = useState(initialRemainCount === null);

    const storyCategories = useMemo(
        () => STORY_KEYWORDS[estateCategory] || STORY_KEYWORDS.residential,
        [estateCategory]
    );

    // 타입 변경 시 키워드 초기화
    useEffect(() => {
        setSelectedKeywords([]);
        if (selectedType === "story") {
            setOpenCategories(
                storyCategories.reduce((acc, cat, i) => {
                    acc[i] = cat.defaultOpen;
                    return acc;
                }, {})
            );
        }
    }, [selectedType, storyCategories]);

    // 남은 횟수 조회
    useEffect(() => {
        if (initialRemainCount !== null) return;
        getData("/sale/ai_remain_count/")
            .then((res) => setRemainCount(res.remain_count ?? 0))
            .catch(() => setRemainCount(0))
            .finally(() => setCountLoading(false));
    }, [initialRemainCount]);

    const toggleCategory = useCallback((index) => {
        setOpenCategories((prev) => ({ ...prev, [index]: !prev[index] }));
    }, []);

    const toggleKeyword = useCallback((keyword) => {
        setSelectedKeywords((prev) => {
            if (prev.includes(keyword)) {
                return prev.filter((k) => k !== keyword);
            }
            const limit = selectedType === "ad" ? MAX_KEYWORDS_AD : MAX_KEYWORDS_STORY;
            if (prev.length >= limit) return prev;
            return [...prev, keyword];
        });
    }, [selectedType]);

    const handleGenerate = useCallback(async () => {
        if (loading || (remainCount <= 0 && selectedType !== "summary")) return;
        setLoading(true);
        try {
            const res = await postJsonData("/sale/ai_generate_description/", {
                property_info: propertyInfo,
                write_type: selectedType,
                e_rand_type: eRandType,
                keywords: selectedType === "summary" ? [] : selectedKeywords,
            });

            // 요약형은 Lambda 없이 즉시 결과 반환
            if (res.description) {
                onGenerated(res.description, res.remain_count ?? remainCount - 1);
                return;
            }

            // story/ad 는 job_id 로 폴링 (3초 × 5회 = 15초)
            if (res.job_id) {
                const nextRemain = res.remain_count ?? remainCount - 1;
                for (let i = 0; i < 5; i++) {
                    await new Promise((r) => setTimeout(r, 3000));
                    const poll = await getData(`/sale/ai_job_status/${res.job_id}/`);
                    if (poll?.status === "done") {
                        onGenerated(poll.description, nextRemain);
                        return;
                    }
                    if (poll?.status === "failed") {
                        toast.error("AI 글쓰기 오류: " + (poll.error || "생성 실패"));
                        return;
                    }
                }
                toast.error("AI 글쓰기가 지연되고 있습니다. 다시 시도해주세요.");
                return;
            }

            if (res.error) {
                toast.error("AI 글쓰기 오류: " + res.error);
            }
        } catch (e) {
            toast.error("AI 글쓰기에 실패했습니다.");
        } finally {
            setLoading(false);
        }
    }, [loading, remainCount, propertyInfo, selectedType, eRandType, selectedKeywords, onGenerated]);

    const loadingDots = useLoadingDots(loading);
    const isDisabled = countLoading || (remainCount <= 0 && selectedType !== "summary");

    return (
        <div className={styles.settingStep}>
            <div className={styles.settingHeader}>
                <h2 className={styles.settingTitle}>AI 매물 글쓰기</h2>
                <button className={styles.closeBtn} onClick={onClose}>
                    <CloseIcon />
                </button>
            </div>

            <div className={styles.settingBody}>
                {/* 1. TYPE 선택 */}
                <div className={styles.section}>
                    <h3 className={styles.sectionTitle}>1. TYPE 선택</h3>
                    <div className={styles.typeList}>
                        {WRITE_TYPES.map((type) => (
                            <button
                                key={type.key}
                                className={`${styles.typeItem} ${selectedType === type.key ? styles.selected : ""}`}
                                onClick={() => setSelectedType(type.key)}
                            >
                                <span className={styles.typeIcon}>{TYPE_ICONS[type.key]}</span>
                                <span className={styles.typeLabel}>{type.label}</span>
                                <span className={styles.typeDesc}>{type.desc}</span>
                            </button>
                        ))}
                    </div>
                </div>

                {/* 스토리형: 카테고리별 키워드 (최대 10개) */}
                {selectedType === "story" && (
                    <div className={styles.section}>
                        <div className={styles.keywordHeader}>
                            <h3 className={styles.sectionTitle}>2. 강조 키워드 선택</h3>
                            <span className={styles.keywordLimitLabel}>(최대 {MAX_KEYWORDS_STORY}개)</span>
                            <span className={styles.keywordCount}>{selectedKeywords.length}/{MAX_KEYWORDS_STORY}</span>
                        </div>
                        {storyCategories.map((cat, catIndex) => (
                            <div key={catIndex} className={styles.keywordCategory}>
                                <button className={styles.categoryHeader} onClick={() => toggleCategory(catIndex)}>
                                    <span>{cat.label}</span>
                                    <ChevronIcon className={`${styles.chevron} ${openCategories[catIndex] ? styles.open : ""}`} />
                                </button>
                                {openCategories[catIndex] && (
                                    <div className={styles.keywordList}>
                                        {cat.keywords.map((kw) => (
                                            <button
                                                key={kw}
                                                className={`${styles.keywordChip} ${selectedKeywords.includes(kw) ? styles.selected : ""}`}
                                                onClick={() => toggleKeyword(kw)}
                                                disabled={!selectedKeywords.includes(kw) && selectedKeywords.length >= MAX_KEYWORDS_STORY}
                                            >
                                                {kw}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {/* 광고형: 플랫 키워드 (최대 5개) */}
                {selectedType === "ad" && (
                    <div className={styles.section}>
                        <div className={styles.keywordHeader}>
                            <h3 className={styles.sectionTitle}>2. 강조 키워드 선택</h3>
                            <span className={styles.keywordLimitLabel}>(최대 {MAX_KEYWORDS_AD}개)</span>
                            <span className={styles.keywordCount}>{selectedKeywords.length}/{MAX_KEYWORDS_AD}</span>
                        </div>
                        <div className={styles.keywordList}>
                            {AD_KEYWORDS.map((kw) => (
                                <button
                                    key={kw}
                                    className={`${styles.keywordChip} ${selectedKeywords.includes(kw) ? styles.selected : ""}`}
                                    onClick={() => toggleKeyword(kw)}
                                    disabled={!selectedKeywords.includes(kw) && selectedKeywords.length >= MAX_KEYWORDS_AD}
                                >
                                    {kw}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* 요약형: 안내 문구 */}
                {selectedType === "summary" && (
                    <div className={styles.summaryNotice}>
                        <p>요약형 문구는</p>
                        <p>매물 정보를 바탕으로 자동 생성됩니다!</p>
                        <p>(요약형 글쓰기는 생성 횟수가 차감되지 않습니다)</p>
                    </div>
                )}
            </div>

            {/* 생성 버튼 */}
            <div className={styles.settingFooter}>
                <button
                    className={`${styles.generateBtn} ${loading ? styles.loading : isDisabled ? styles.disabled : ""}`}
                    onClick={handleGenerate}
                    disabled={isDisabled || loading}
                >
                    <SparkleIcon color="white" />
                    <span>
                        {loading ? `생성중${loadingDots}` : countLoading ? "로딩중..." : `문구 생성하기 (${remainCount}회 남음)`}
                    </span>
                </button>
            </div>
        </div>
    );
};

export default SettingStep;
